import _ from "lodash";
import i18n from "@/plugins/i18n";
import { DataOptions } from "vuetify";
import { defineModule } from "direct-vuex";
import { FILTER_NAMES } from "@/static/filterNames";
import ContractsEditor from "@/store/modules/contracts/contractsEditor";
import { moduleActionContext, moduleGetterContext } from "@/store/index";
import { COST_DOCUMENT_CONTROLLERS } from "@/static/costDocumentControllers";
import { Contract, ContractResponse } from "@/models/api/contracts/Contract";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import { deleteContract, getContractComponents, getContracts } from "@/requests/contract";
import { ContractComponent, ContractComponentResponse } from "@/models/api/contracts/ContractComponent";

export interface ContractsStateInterface {
  contractsList: Array<Contract>;
  contractsCount: number;
  contractsOptions: DataOptions;
  contractsFilters: Array<CriterionItemRequest> | null;
  selectedContract: Contract | null;
  isContractsLoading: boolean;
  activeView: COST_DOCUMENT_CONTROLLERS;
  contractFilterName: string;

  componentsList: Array<ContractComponent>;
  componentsCount: number;
  componentsOptions: DataOptions;
  componentsFilters: Array<CriterionItemRequest> | null;
  selectedComponent: ContractComponent | null;
  isComponentsLoading: boolean;
}

export const ContractsState: ContractsStateInterface = {
  contractsList: [],
  contractsCount: 0,
  contractsOptions: new DefaultDataOptions(),
  contractsFilters: null,
  selectedContract: null,
  isContractsLoading: false,
  activeView: COST_DOCUMENT_CONTROLLERS.Other,
  contractFilterName: FILTER_NAMES.ContractStandardFilter,

  componentsList: [],
  componentsCount: 0,
  componentsOptions: new DefaultDataOptions(),
  componentsFilters: null,
  selectedComponent: null,
  isComponentsLoading: false,
};

const DeveloperModule = defineModule({
  state: ContractsState as ContractsStateInterface,
  namespaced: true as const,
  modules: {
    editor: ContractsEditor,
  },
  getters: {},
  mutations: {
    setContractsList(state, param: ContractResponse) {
      state.contractsList = param.items;
      state.contractsCount = param.totalCount;
    },
    setContractsOptions(state, param: DataOptions) {
      state.contractsOptions = JSON.parse(JSON.stringify(param));
    },
    setContractsFilters(state, param: Array<CriterionItemRequest>) {
      state.contractsFilters = param;
    },
    setSelectedContract(state, param: Contract | null) {
      state.selectedContract = param;
    },
    setIsContractsLoading(state, param: boolean) {
      state.isContractsLoading = param;
    },
    setActiveView(state, param: COST_DOCUMENT_CONTROLLERS) {
      if (state.activeView === param) return;
      state.activeView = param;
      state.contractFilterName =
        param === COST_DOCUMENT_CONTROLLERS.Other
          ? FILTER_NAMES.ContractStandardFilter
          : FILTER_NAMES.CommercialContractStandardFilter;
      state.contractsOptions = new DefaultDataOptions();
      state.contractsCount = 0;
      state.contractsList = [];
      state.selectedContract = null;

      state.componentsOptions = new DefaultDataOptions();
      state.componentsCount = 0;
      state.componentsList = [];
      state.selectedComponent = null;
    },

    setComponentsList(state, param: ContractComponentResponse) {
      state.componentsList = param.items;
      state.componentsCount = param.totalCount;
    },
    setComponentsOptions(state, param: DataOptions) {
      state.componentsOptions = JSON.parse(JSON.stringify(param));
    },
    setComponentsFilters(state, param: Array<CriterionItemRequest>) {
      state.componentsFilters = param;
    },
    setSelectedComponent(state, param: ContractComponent | null) {
      state.selectedComponent = param;
    },
    setIsComponentsLoading(state, param: boolean) {
      state.isComponentsLoading = param;
    },
  },
  actions: {
    async getContracts(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsContractsLoading(true);
      const response = await getContracts({
        count: state.contractsOptions.itemsPerPage,
        page: state.contractsOptions.page - 1,
        criterions: _.cloneDeep(state.contractsFilters),
        filterName: state.contractFilterName,
      });
      commit.setContractsList(response);
      commit.setIsContractsLoading(false);
      if (response.items.length > 0) commit.setSelectedContract(response.items[0]);
    },
    async deleteContract(context, contractId: number) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      if (await rootDispatch.popup.addConfirmWithMessage(i18n.t("deleteContractQuestion"))) {
        await deleteContract(contractId);
        await dispatch.getContracts();
        await rootDispatch.popup.addSavedOkSnackbar();
      }
    },
    async getComponents(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedContract) {
        commit.setIsComponentsLoading(true);
        const response = await getContractComponents(
          {
            count: state.componentsOptions.itemsPerPage,
            page: state.componentsOptions.page - 1,
            criterions: _.cloneDeep(state.componentsFilters),
            filterName: FILTER_NAMES.ContractComponentStandardFilter,
          },
          state.selectedContract.id
        );
        commit.setComponentsList(response);
        commit.setIsComponentsLoading(false);
        commit.setSelectedComponent(null);
      }
    },
  },
});

export default DeveloperModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, DeveloperModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, DeveloperModule);
